import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tripAPI } from '../../api/trips';
import { paymentAPI } from '../../api/payments';
import { Trip, Payment, TripFuelDetails, PaymentParty } from '../../types/entities';
import { Edit, ArrowLeft, Plus, Trash2, Fuel, AlertTriangle, CheckCircle, Play, XCircle, RotateCcw, FileText, Warehouse } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { DocumentList, ReceiptButton, DocumentUpload } from '../../components/Documents';
import { ReceiptConfirmationModal } from '../../components/warehouse/ReceiptConfirmationModal';
import { LeftoverWarehouseModal } from '../../components/Trip/LeftoverWarehouseModal';
import { calculateLeftoverItems, isDirectTrip, LeftoverItem } from '../../utils/trip-leftover-calculator';
import { Package } from 'lucide-react';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN').format(value);
};

export const TripDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [fuelDetails, setFuelDetails] = useState<TripFuelDetails | null>(null);
  const [loading, setLoading] = useState(true);

  // Payment form state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    method: 'CASH',
    paymentParty: 'SUPPLIER' as PaymentParty,
    note: '',
  });
  const [createdPaymentId, setCreatedPaymentId] = useState<number | null>(null);
  const [paymentDocumentRefreshKeys, setPaymentDocumentRefreshKeys] = useState<Record<number, number>>({});
  const [tripDocumentRefreshKey, setTripDocumentRefreshKey] = useState(0);
  // Track which payment rows have the upload panel open
  const [openUploadPaymentIds, setOpenUploadPaymentIds] = useState<Set<number>>(new Set());

  // Phase 2: Warehouse receipt modal
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  // Leftover goods modal for direct trips
  const [showLeftoverModal, setShowLeftoverModal] = useState(false);
  const [leftoverItems, setLeftoverItems] = useState<LeftoverItem[]>([]);
  const [completingLoading, setCompletingLoading] = useState(false);

  const getUnitLabel = (unit: string) => t(`items.units.${unit}`, { defaultValue: unit });
  const getPaymentMethodLabel = (method: string) => t(`payments.methods.${method}`, { defaultValue: method });

  useEffect(() => {
    if (id) {
      loadData(parseInt(id));
    }
  }, [id]);

  const loadData = async (tripId: number) => {
    try {
      const [tripData, paymentsData, fuelData] = await Promise.all([
        tripAPI.getById(tripId),
        paymentAPI.getByTrip(tripId),
        tripAPI.getFuelDetails(tripId).catch(() => null),
      ]);
      setTrip(tripData);
      setPayments(paymentsData);
      setFuelDetails(fuelData);
    } catch (error) {
      toast.error(t('trips.loadError'));
      navigate('/trips');
    } finally {
      setLoading(false);
    }
  };

  // Use backend-calculated values from Trip entity instead of recalculating
  const totalPaid = trip?.paidAmount || 0;
  const outstanding = trip?.outstandingAmount || 0;

  // Calculate paid amounts per party from payments list
  const paidBySupplier = payments
    .filter((p) => p.paymentParty === 'SUPPLIER')
    .reduce((sum, p) => sum + Number(p.amount), 0);
  const paidByCustomer = payments
    .filter((p) => p.paymentParty === 'CUSTOMER')
    .reduce((sum, p) => sum + Number(p.amount), 0);

  // Currency format input helpers
  const [amountDisplay, setAmountDisplay] = useState('');

  const handleAmountChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    const numeric = digits ? parseInt(digits, 10) : 0;
    setAmountDisplay(numeric > 0 ? new Intl.NumberFormat('vi-VN').format(numeric) : '');
    setPaymentData((prev) => ({ ...prev, amount: digits }));
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !trip) return;

    const amount = parseFloat(paymentData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error(t('validation.positiveNumber'));
      return;
    }

    // Validate against per-party outstanding
    const pt = trip.pickupType;
    const dt = trip.deliveryType;
    const purchaseValue = Number(trip.totalPurchaseValue ?? trip.totalValue);
    const saleValue = Number(trip.totalSaleValue ?? trip.totalValue);
    const outstandingForParty =
      paymentData.paymentParty === 'SUPPLIER'
        ? Math.max(0, purchaseValue - paidBySupplier)
        : Math.max(0, saleValue - paidByCustomer);
    // For non-direct trips fallback to overall outstanding
    const isDirect = pt === 'SUPPLIER' && dt === 'CUSTOMER';
    const effectiveOutstanding = isDirect ? outstandingForParty : outstanding;

    if (amount > effectiveOutstanding) {
      toast.error(t('payments.exceedsTotal'));
      return;
    }

    try {
      const newPayment = await paymentAPI.create(parseInt(id), {
        amount,
        method: paymentData.method,
        paymentParty: paymentData.paymentParty,
        notes: paymentData.note || undefined,
      });
      toast.success(t('payments.createSuccess'));
      setCreatedPaymentId(newPayment.id);
      // Keep form open to allow document upload
      // setShowPaymentForm(false);
      // Don't clear form yet - allow user to upload documents
      // setPaymentData({ amount: '', method: 'CASH', note: '' });
      loadData(parseInt(id));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    }
  };

  const handleDeletePayment = async (paymentId: number) => {
    if (!window.confirm(t('common.confirm'))) return;
    if (!id) return;

    try {
      await paymentAPI.delete(paymentId);
      toast.success(t('payments.deleteSuccess'));
      loadData(parseInt(id));
    } catch (error) {
      toast.error(t('errors.unknown'));
    }
  };

  const handleUpdateStatus = async (newStatus: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED') => {
    if (!id || !trip) return;

    // Phase 2: Show receipt modal for warehouse delivery completion
    if (newStatus === 'COMPLETED' && trip.deliveryType === 'WAREHOUSE') {
      setShowReceiptModal(true);
      return;
    }

    // Direct trip completion: detect leftover goods
    if (newStatus === 'COMPLETED' && isDirectTrip(trip) && trip.items) {
      const leftovers = calculateLeftoverItems(trip.items);
      if (leftovers.length > 0) {
        setLeftoverItems(leftovers);
        setShowLeftoverModal(true);
        return;
      }
    }

    const confirmMessages: Record<string, string> = {
      'IN_PROGRESS': t('trips.confirmStart'),
      'COMPLETED': t('trips.confirmComplete'),
      'CANCELLED': t('trips.confirmCancel'),
      'PENDING': t('trips.confirmReactivate'),
    };

    if (!window.confirm(confirmMessages[newStatus] || t('common.confirm'))) return;

    try {
      if (newStatus === 'COMPLETED') {
        // Use new complete endpoint
        await tripAPI.complete(parseInt(id), false);
      } else {
        await tripAPI.update(parseInt(id), { status: newStatus });
      }
      toast.success(t('trips.updateSuccess'));
      loadData(parseInt(id));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    }
  };

  // Leftover modal handlers
  const handleLeftoverConfirm = async (warehouseId: number) => {
    if (!id) return;
    setCompletingLoading(true);
    try {
      await tripAPI.complete(parseInt(id), false, warehouseId);
      toast.success(t('trips.leftover.successEntered'));
      setShowLeftoverModal(false);
      loadData(parseInt(id));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    } finally {
      setCompletingLoading(false);
    }
  };

  const handleLeftoverSkip = async () => {
    if (!id) return;
    setCompletingLoading(true);
    try {
      await tripAPI.complete(parseInt(id), false);
      toast.success(t('trips.leftover.successSkipped'));
      setShowLeftoverModal(false);
      loadData(parseInt(id));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    } finally {
      setCompletingLoading(false);
    }
  };

  // Phase 2: Handle receipt confirmation
  const handleReceiptConfirm = async () => {
    if (!id) return;

    try {
      await tripAPI.complete(parseInt(id), true);
      toast.success(t('warehouse.receiptConfirmation.successReceipted'));
      setShowReceiptModal(false);
      loadData(parseInt(id));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    }
  };

  const handleReceiptSkip = async () => {
    if (!id) return;

    try {
      await tripAPI.complete(parseInt(id), false);
      toast.success(t('warehouse.receiptConfirmation.successDeferred'));
      setShowReceiptModal(false);
      loadData(parseInt(id));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    }
  };

  // Phase 2: Handle deferred receipt to warehouse
  const handleReceiptToWarehouse = async () => {
    if (!id || !window.confirm(t('common.confirm'))) return;

    try {
      await tripAPI.receiptToWarehouse(parseInt(id));
      toast.success(t('warehouse.receiptConfirmation.successReceipted'));
      loadData(parseInt(id));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    }
  };

  // Get status badge color
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      'PENDING': 'bg-yellow-100 text-yellow-700',
      'IN_PROGRESS': 'bg-blue-100 text-blue-700',
      'COMPLETED': 'bg-green-100 text-green-700',
      'CANCELLED': 'bg-red-100 text-red-700',
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) return <div className="text-center py-10">{t('common.loading')}</div>;
  if (!trip) return <div className="text-center py-10">{t('errors.notFound')}</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/trips')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">{t('trips.viewTrip')} #{trip.id}</h1>
            <div className="flex gap-2 mt-1">
              <span
                className={`inline-block px-3 py-1 text-sm rounded-full ${getStatusBadge(trip.status)}`}
              >
                {t(`trips.statuses.${trip.status}`)}
              </span>
              {/* Phase 2: Warehouse receipt status badge */}
              {trip.warehouseReceiptStatus === 'PENDING' && (
                <span className="inline-block px-3 py-1 text-sm rounded-full bg-orange-100 text-orange-700">
                  {t('warehouse.receiptStatus.pending')}
                </span>
              )}
              {trip.warehouseReceiptStatus === 'RECEIPTED' && (
                <span className="inline-block px-3 py-1 text-sm rounded-full bg-green-100 text-green-700">
                  {t('warehouse.receiptStatus.receipted')}
                </span>
              )}
              {trip.leftoverStatus === 'PENDING_MANUAL' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full bg-amber-100 text-amber-700">
                  <Warehouse className="w-3.5 h-3.5" />
                  {t('trips.leftover.statusPending')}
                </span>
              )}
              {trip.leftoverStatus === 'ENTERED' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full bg-teal-100 text-teal-700">
                  <Warehouse className="w-3.5 h-3.5" />
                  {t('trips.leftover.statusEntered')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {/* Phase 2: Receipt to warehouse button for pending receipt trips */}
          {trip.status === 'COMPLETED' && trip.warehouseReceiptStatus === 'PENDING' && (
            <button
              onClick={handleReceiptToWarehouse}
              className="btn-primary flex items-center"
            >
              <Package className="w-4 h-4 mr-1" />
              {t('warehouse.receiptToWarehouse')}
            </button>
          )}

          {/* Status transition buttons based on current status */}
          {trip.status === 'PENDING' && (
            <>
              <button
                onClick={() => handleUpdateStatus('IN_PROGRESS')}
                className="btn-secondary text-blue-600 hover:bg-blue-50 flex items-center"
              >
                <Play className="w-4 h-4 mr-1" />
                {t('trips.startTrip')}
              </button>
              <button
                onClick={() => handleUpdateStatus('CANCELLED')}
                className="btn-secondary text-red-600 hover:bg-red-50 flex items-center"
              >
                <XCircle className="w-4 h-4 mr-1" />
                {t('trips.cancelTrip')}
              </button>
            </>
          )}
          {trip.status === 'IN_PROGRESS' && (
            <>
              <button
                onClick={() => handleUpdateStatus('COMPLETED')}
                className="btn-secondary text-green-600 hover:bg-green-50 flex items-center"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                {t('trips.completeTrip')}
              </button>
              <button
                onClick={() => handleUpdateStatus('CANCELLED')}
                className="btn-secondary text-red-600 hover:bg-red-50 flex items-center"
              >
                <XCircle className="w-4 h-4 mr-1" />
                {t('trips.cancelTrip')}
              </button>
            </>
          )}
          {trip.status === 'COMPLETED' && (
            <button
              onClick={() => handleUpdateStatus('PENDING')}
              className="btn-secondary text-yellow-600 hover:bg-yellow-50 flex items-center"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              {t('trips.reopenTrip')}
            </button>
          )}
          {trip.status === 'CANCELLED' && (
            <button
              onClick={() => handleUpdateStatus('PENDING')}
              className="btn-secondary text-yellow-600 hover:bg-yellow-50 flex items-center"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              {t('trips.reactivateTrip')}
            </button>
          )}
          <Link
            to={`/trips/${trip.id}/edit`}
            className="btn-primary flex items-center"
          >
            <Edit className="w-4 h-4 mr-2" />
            {t('common.edit')}
          </Link>
        </div>
      </div>

      {/* Basic Info */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">{t('trips.basicInfo')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">{t('trips.date')}</p>
            <p className="font-medium">{format(new Date(trip.date), 'dd/MM/yyyy')}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('trips.vehicle')}</p>
            <p className="font-medium">{trip.vehicle?.licensePlate || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('trips.supplier')}</p>
            <p className="font-medium">{trip.supplier?.name || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('trips.customer')}</p>
            <p className="font-medium">{trip.customer?.name || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('trips.driver')}</p>
            <p className="font-medium">{trip.driver?.name || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('trips.distance')}</p>
            <p className="font-medium">{trip.distanceKm ? `${trip.distanceKm} ${t('trips.distanceUnit')}` : '-'}</p>
          </div>
          {trip.driverId && (
            <div>
              <p className="text-sm text-gray-500">{t('trips.driverWage')}</p>
              <p className="font-medium">
                {trip.driverWage != null
                  ? `${new Intl.NumberFormat('vi-VN').format(Number(trip.driverWage))} đ`
                  : '-'}
              </p>
            </div>
          )}
          <div className="col-span-2">
            <p className="text-sm text-gray-500">{t('trips.deliveryAddress')}</p>
            <p className="font-medium">{trip.deliveryAddress || '-'}</p>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">{t('trips.items')}</h2>
        {(() => {
          // Phát hiện chuyến trực tiếp: có items với role PURCHASE hoặc SALE riêng biệt
          const purchaseItems = trip.items?.filter(i => i.itemRole === 'PURCHASE') ?? [];
          const saleItems = trip.items?.filter(i => i.itemRole === 'SALE') ?? [];
          const isDirectTrip = purchaseItems.length > 0 || saleItems.length > 0;

          // Legacy detection: hasBothPrices = BOTH items with both purchasePrice and salePrice
          const legacyBothItems = trip.items?.filter(i => !i.itemRole || i.itemRole === 'BOTH') ?? [];
          const hasBothPrices = legacyBothItems.some(i => i.purchasePrice != null && i.salePrice != null);

          if (isDirectTrip) {
            // Render 2 tables: hàng nhập và hàng giao
            const renderItemsTable = (
              tableItems: typeof trip.items,
              title: string,
              priceLabel: string,
              colorClass: string,
              priceKey: 'purchasePrice' | 'salePrice',
              total: number
            ) => (
              <div className="mb-4">
                <h3 className={`font-semibold text-sm mb-2 ${colorClass}`}>{title}</h3>
                {tableItems && tableItems.length > 0 ? (
                  <table className="w-full">
                    <thead className="border-b">
                      <tr className="text-left">
                        <th className="pb-2 table-header">{t('trips.itemName')}</th>
                        <th className="pb-2 table-header text-right">{t('trips.volumeWeight')}</th>
                        <th className="pb-2 table-header">{t('common.unit')}</th>
                        <th className={`pb-2 table-header text-right ${colorClass}`}>{priceLabel}</th>
                        <th className="pb-2 table-header text-right">{t('trips.lineTotal')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableItems.map((item) => (
                        <tr key={item.id} className="border-b last:border-0">
                          <td className="py-2">
                            <div className="flex flex-wrap items-center gap-1">
                              <span>{item.itemName}</span>
                              {item.isGiftFromSupplier && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                                  {t('trips.giftFromSupplierBadge')}
                                </span>
                              )}
                              {item.isGiftToCustomer && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                                  {t('trips.giftToCustomerBadge')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2 text-right">{item.volumeWeight}</td>
                          <td className="py-2">{getUnitLabel(item.unit)}</td>
                          <td className={`py-2 text-right ${colorClass}`}>
                            {item[priceKey] != null ? formatCurrency(item[priceKey] as number) : formatCurrency(item.unitPrice)}
                          </td>
                          <td className="py-2 text-right font-medium">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t">
                      <tr>
                        <td colSpan={4} className={`py-1 text-right text-sm font-semibold ${colorClass}`}>{priceLabel} tổng:</td>
                        <td className={`py-1 text-right font-semibold ${colorClass}`}>{formatCurrency(total)} đ</td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <p className="text-sm text-gray-400 italic">Không có hàng</p>
                )}
              </div>
            );

            return (
              <>
                {renderItemsTable(purchaseItems, '📦 Hàng nhập từ NCC', t('trips.purchasePrice'), 'text-orange-600', 'purchasePrice', trip.totalPurchaseValue ?? 0)}
                {renderItemsTable(saleItems, '🚚 Hàng giao cho KH', t('trips.salePrice'), 'text-blue-600', 'salePrice', trip.totalSaleValue ?? 0)}
              </>
            );
          }

          return (
            <table className="w-full">
              <thead className="border-b">
                <tr className="text-left">
                  <th className="pb-3 table-header">{t('trips.itemName')}</th>
                  <th className="pb-3 table-header text-right">{t('trips.volumeWeight')}</th>
                  <th className="pb-3 table-header">{t('common.unit')}</th>
                  {hasBothPrices ? (
                    <>
                      <th className="pb-3 table-header text-right text-orange-600">{t('trips.purchasePrice')}</th>
                      <th className="pb-3 table-header text-right text-blue-600">{t('trips.salePrice')}</th>
                    </>
                  ) : (
                    <th className="pb-3 table-header text-right">{t('trips.unitPrice')}</th>
                  )}
                  <th className="pb-3 table-header text-right">{t('trips.lineTotal')}</th>
                </tr>
              </thead>
              <tbody>
                {legacyBothItems.map((item) => (
                  <tr key={item.id} className="border-b last:border-0">
                    <td className="py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <span>{item.itemName}</span>
                        {item.isGiftFromSupplier && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                            {t('trips.giftFromSupplierBadge')}
                          </span>
                        )}
                        {item.isGiftToCustomer && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                            {t('trips.giftToCustomerBadge')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 text-right">{item.volumeWeight}</td>
                    <td className="py-3">{getUnitLabel(item.unit)}</td>
                    {hasBothPrices ? (
                      <>
                        <td className="py-3 text-right text-orange-600">{item.purchasePrice != null ? formatCurrency(item.purchasePrice) : '-'}</td>
                        <td className="py-3 text-right text-blue-600">{item.salePrice != null ? formatCurrency(item.salePrice) : '-'}</td>
                      </>
                    ) : (
                      <td className="py-3 text-right">{formatCurrency(item.unitPrice)}</td>
                    )}
                    <td className="py-3 text-right font-medium">
                      {formatCurrency(item.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t">
                {hasBothPrices ? (
                  <>
                    <tr>
                      <td colSpan={5} className="py-1 text-right text-sm text-orange-600">
                        {t('trips.totalPurchaseValue')}:
                      </td>
                      <td className="py-1 text-right font-semibold text-orange-600">
                        {formatCurrency(trip.totalPurchaseValue ?? 0)} đ
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={5} className="py-1 text-right text-sm text-blue-600">
                        {t('trips.totalSaleValue')}:
                      </td>
                      <td className="py-1 text-right font-semibold text-blue-600">
                        {formatCurrency(trip.totalSaleValue ?? 0)} đ
                      </td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={4} className="py-3 text-right font-semibold">
                      {t('trips.totalValue')}:
                    </td>
                    <td className="py-3 text-right text-lg font-bold text-primary-600">
                      {formatCurrency(trip.totalValue)} đ
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          );
        })()}
      </div>

      {/* Fuel Consumption Section */}
      {fuelDetails && (fuelDetails.fuelConsumed > 0 || fuelDetails.expectedFuel > 0) && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Fuel className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold">{t('fuel.title')}</h2>
            </div>
            {fuelDetails.isOverNorm ? (
              <span className="flex items-center text-red-600 text-sm">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {t('fuel.overNorm')} ({fuelDetails.variancePercent.toFixed(1)}%)
              </span>
            ) : fuelDetails.fuelConsumed > 0 ? (
              <span className="flex items-center text-green-600 text-sm">
                <CheckCircle className="w-4 h-4 mr-1" />
                {t('fuel.withinNorm')}
              </span>
            ) : null}
          </div>

          {/* Fuel Summary Cards */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className={`p-4 rounded-lg ${fuelDetails.isOverNorm ? 'bg-red-50' : 'bg-blue-50'}`}>
              <p className="text-sm text-gray-600">{t('fuel.fuelConsumed')}</p>
              <p className={`text-2xl font-bold ${fuelDetails.isOverNorm ? 'text-red-600' : 'text-blue-600'}`}>
                {fuelDetails.fuelConsumed.toFixed(1)} L
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">{t('fuel.expectedFuel')}</p>
              <p className="text-2xl font-bold text-gray-700">
                {fuelDetails.expectedFuel.toFixed(1)} L
              </p>
              <p className="text-xs text-gray-500">
                ({fuelDetails.distanceKm} km × {fuelDetails.fuelNorm} L/100km)
              </p>
            </div>
            <div className={`p-4 rounded-lg ${
              fuelDetails.fuelVariance > 0 ? 'bg-red-50' : 'bg-green-50'
            }`}>
              <p className="text-sm text-gray-600">{t('fuel.fuelVariance')}</p>
              <p className={`text-2xl font-bold ${
                fuelDetails.fuelVariance > 0 ? 'text-red-600' : 'text-green-600'
              }`}>
                {fuelDetails.fuelVariance > 0 ? '+' : ''}{fuelDetails.fuelVariance.toFixed(1)} L
              </p>
              <p className={`text-xs ${
                fuelDetails.fuelVariance > 0 ? 'text-red-500' : 'text-green-500'
              }`}>
                {fuelDetails.variancePercent > 0 ? '+' : ''}{fuelDetails.variancePercent.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Fuel Expenses List */}
          {fuelDetails.fuelExpenses.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">{t('fuel.fuelExpenses')}</h3>
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr className="text-left">
                    <th className="pb-2">{t('trips.date')}</th>
                    <th className="pb-2 text-right">{t('expenses.quantity')} (L)</th>
                    <th className="pb-2 text-right">{t('expenses.unitPrice')}</th>
                    <th className="pb-2 text-right">{t('expenses.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {fuelDetails.fuelExpenses.map((expense) => (
                    <tr key={expense.id} className="border-b last:border-0">
                      <td className="py-2">
                        {format(new Date(expense.date), 'dd/MM/yyyy')}
                      </td>
                      <td className="py-2 text-right">{expense.quantity.toFixed(1)}</td>
                      <td className="py-2 text-right">{formatCurrency(expense.unitPrice)}</td>
                      <td className="py-2 text-right font-medium">
                        {formatCurrency(expense.amount)} đ
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {fuelDetails.fuelExpenses.length === 0 && fuelDetails.expectedFuel > 0 && (
            <p className="text-center text-gray-500 py-4">{t('fuel.noFuelData')}</p>
          )}
        </div>
      )}

      {/* Payments */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">{t('payments.title')}</h2>
          {outstanding > 0 && (
            <button
              onClick={() => {
                // Auto-set paymentParty based on trip type
                const pt = trip.pickupType;
                const dt = trip.deliveryType;
                const isExportOnly = pt === 'WAREHOUSE' && dt === 'CUSTOMER';
                const defaultParty: PaymentParty = isExportOnly ? 'CUSTOMER' : 'SUPPLIER';
                setPaymentData(prev => ({ ...prev, paymentParty: defaultParty }));
                setShowPaymentForm(true);
              }}
              className="btn-primary flex items-center text-sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('payments.addPayment')}
            </button>
          )}
        </div>

        {/* Payment Summary — split by trip type */}
        {(() => {
          const pt = trip.pickupType;
          const dt = trip.deliveryType;
          const isDirect = pt === 'SUPPLIER' && dt === 'CUSTOMER';
          const isImport = pt === 'SUPPLIER' && dt === 'WAREHOUSE';
          const isExport = pt === 'WAREHOUSE' && dt === 'CUSTOMER';

          const purchaseValue = Number(trip.totalPurchaseValue ?? trip.totalValue);
          const saleValue = Number(trip.totalSaleValue ?? trip.totalValue);

          if (isDirect) {
            const outstandingNCC = Math.max(0, purchaseValue - paidBySupplier);
            const outstandingKH = Math.max(0, saleValue - paidByCustomer);
            return (
              <div className="space-y-3 mb-4">
                {/* Supplier payment */}
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                  <p className="text-xs font-semibold text-orange-700 mb-2">{t('debt.supplierDebt')} (NCC)</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <p className="text-xs text-gray-500">{t('trips.totalPurchaseValue')}</p>
                      <p className="font-semibold text-orange-700">{formatCurrency(purchaseValue)} đ</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('trips.paidAmount')}</p>
                      <p className="font-semibold text-green-600">{formatCurrency(paidBySupplier)} đ</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('trips.outstanding')}</p>
                      <p className={`font-semibold ${outstandingNCC > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(outstandingNCC)} đ
                      </p>
                    </div>
                  </div>
                </div>
                {/* Customer collection */}
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs font-semibold text-blue-700 mb-2">{t('debt.customerDebt')} (KH)</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <p className="text-xs text-gray-500">{t('trips.totalSaleValue')}</p>
                      <p className="font-semibold text-blue-700">{formatCurrency(saleValue)} đ</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('trips.paidAmount')}</p>
                      <p className="font-semibold text-green-600">{formatCurrency(paidByCustomer)} đ</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('trips.outstanding')}</p>
                      <p className={`font-semibold ${outstandingKH > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(outstandingKH)} đ
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          if (isImport) {
            const outstandingNCC = Math.max(0, purchaseValue - paidBySupplier);
            return (
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-100 mb-4">
                <p className="text-xs font-semibold text-orange-700 mb-2">{t('debt.supplierDebt')} (NCC)</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-xs text-gray-500">{t('trips.totalPurchaseValue')}</p>
                    <p className="font-semibold text-orange-700">{formatCurrency(purchaseValue)} đ</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('trips.paidAmount')}</p>
                    <p className="font-semibold text-green-600">{formatCurrency(paidBySupplier)} đ</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('trips.outstanding')}</p>
                    <p className={`font-semibold ${outstandingNCC > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(outstandingNCC)} đ
                    </p>
                  </div>
                </div>
              </div>
            );
          }

          if (isExport) {
            const outstandingKH = Math.max(0, saleValue - paidByCustomer);
            return (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 mb-4">
                <p className="text-xs font-semibold text-blue-700 mb-2">{t('debt.customerDebt')} (KH)</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-xs text-gray-500">{t('trips.totalSaleValue')}</p>
                    <p className="font-semibold text-blue-700">{formatCurrency(saleValue)} đ</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('trips.paidAmount')}</p>
                    <p className="font-semibold text-green-600">{formatCurrency(paidByCustomer)} đ</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('trips.outstanding')}</p>
                    <p className={`font-semibold ${outstandingKH > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(outstandingKH)} đ
                    </p>
                  </div>
                </div>
              </div>
            );
          }

          // Fallback: generic summary
          return (
            <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">{t('trips.totalValue')}</p>
                <p className="text-lg font-semibold">{formatCurrency(trip.totalValue)} đ</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('trips.paidAmount')}</p>
                <p className="text-lg font-semibold text-green-600">{formatCurrency(totalPaid)} đ</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('trips.outstanding')}</p>
                <p className={`text-lg font-semibold ${outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(outstanding)} đ
                </p>
              </div>
            </div>
          );
        })()}

        {/* Payment Form */}
        {showPaymentForm && (() => {
          const pt = trip.pickupType;
          const dt = trip.deliveryType;
          const isDirect = pt === 'SUPPLIER' && dt === 'CUSTOMER';
          const isImportOnly = pt === 'SUPPLIER' && dt === 'WAREHOUSE';

          return (
          <form onSubmit={handleAddPayment} className="mb-4 p-4 border rounded-lg bg-gray-50">
            {/* Payment party indicator / selector */}
            {isDirect ? (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">{t('payments.paymentParty')}</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setPaymentData(prev => ({ ...prev, paymentParty: 'SUPPLIER' }))}
                    className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                      paymentData.paymentParty === 'SUPPLIER'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300'
                    }`}
                  >
                    <span className="block text-xs opacity-70 mb-0.5">💸</span>
                    {t('payments.partyLabels.SUPPLIER')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentData(prev => ({ ...prev, paymentParty: 'CUSTOMER' }))}
                    className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                      paymentData.paymentParty === 'CUSTOMER'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300'
                    }`}
                  >
                    <span className="block text-xs opacity-70 mb-0.5">💰</span>
                    {t('payments.partyLabels.CUSTOMER')}
                  </button>
                </div>
              </div>
            ) : (
              <div className={`mb-4 px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 ${
                isImportOnly
                  ? 'bg-orange-50 text-orange-700 border border-orange-200'
                  : 'bg-blue-50 text-blue-700 border border-blue-200'
              }`}>
                <span>{isImportOnly ? '💸' : '💰'}</span>
                {isImportOnly
                  ? t('payments.partyLabels.SUPPLIER')
                  : t('payments.partyLabels.CUSTOMER')}
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('payments.amount')}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={amountDisplay}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  className="input-field"
                  placeholder={`Max: ${formatCurrency(
                    (() => {
                      const pt2 = trip.pickupType;
                      const dt2 = trip.deliveryType;
                      const isDirect2 = pt2 === 'SUPPLIER' && dt2 === 'CUSTOMER';
                      if (isDirect2) {
                        const pv = Number(trip.totalPurchaseValue ?? trip.totalValue);
                        const sv = Number(trip.totalSaleValue ?? trip.totalValue);
                        return paymentData.paymentParty === 'SUPPLIER'
                          ? Math.max(0, pv - paidBySupplier)
                          : Math.max(0, sv - paidByCustomer);
                      }
                      return outstanding;
                    })()
                  )} đ`}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('payments.method')}
                </label>
                <select
                  value={paymentData.method}
                  onChange={(e) =>
                    setPaymentData({ ...paymentData, method: e.target.value })
                  }
                  className="input-field"
                >
                  <option value="CASH">{t('payments.methods.CASH')}</option>
                  <option value="TRANSFER">{t('payments.methods.TRANSFER')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('payments.note')}</label>
                <input
                  type="text"
                  value={paymentData.note}
                  onChange={(e) =>
                    setPaymentData({ ...paymentData, note: e.target.value })
                  }
                  className="input-field"
                  placeholder={t('payments.note')}
                />
              </div>
            </div>

            {/* Document Upload for Payment */}
            {createdPaymentId && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  {t('documents.paymentDocuments')}
                </p>
                <DocumentUpload
                  entityType="payment"
                  entityId={createdPaymentId}
                  documentType="PAYMENT_PROOF"
                  onUploadComplete={() => {
                    setPaymentDocumentRefreshKeys(prev => ({
                      ...prev,
                      [createdPaymentId]: (prev[createdPaymentId] || 0) + 1
                    }));
                  }}
                />
              </div>
            )}

            <div className="flex justify-end space-x-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowPaymentForm(false);
                  setPaymentData({ amount: '', method: 'CASH', paymentParty: 'SUPPLIER', note: '' });
                  setAmountDisplay('');
                  setCreatedPaymentId(null);
                }}
                className="btn-secondary"
              >
                {createdPaymentId ? t('common.done') : t('common.cancel')}
              </button>
              {!createdPaymentId && (
                <button type="submit" className="btn-primary">
                  {t('common.confirm')}
                </button>
              )}
            </div>
          </form>
          );
        })()}

        {/* Payment List */}
        {payments.length > 0 ? (
          <div className="space-y-4">
            <table className="w-full">
              <thead className="border-b">
                <tr className="text-left">
                  <th className="pb-3 table-header">{t('payments.paidAt')}</th>
                  <th className="pb-3 table-header">{t('payments.paymentParty')}</th>
                  <th className="pb-3 table-header">{t('payments.method')}</th>
                  <th className="pb-3 table-header text-right">{t('payments.amount')}</th>
                  <th className="pb-3 table-header">{t('payments.note')}</th>
                  <th className="pb-3 table-header"></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id} className="border-b last:border-0">
                    <td className="py-3">
                      {payment.date ? format(new Date(payment.date), 'dd/MM/yyyy HH:mm') : '-'}
                    </td>
                    <td className="py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        payment.paymentParty === 'SUPPLIER'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {t(`payments.partyTypes.${payment.paymentParty}`)}
                      </span>
                    </td>
                    <td className="py-3">
                      {getPaymentMethodLabel(payment.method)}
                    </td>
                    <td className="py-3 text-right font-medium">
                      {formatCurrency(payment.amount)} đ
                    </td>
                    <td className="py-3 text-gray-500">{payment.notes || 'N/A'}</td>
                    <td className="py-3">
                      <div className="flex items-center space-x-1">
                        <ReceiptButton type="payment" entityId={payment.id} />
                        <button
                          onClick={() => handleDeletePayment(payment.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Show documents for each payment */}
            {payments.map((payment) => {
              const isUploadOpen = openUploadPaymentIds.has(payment.id);
              return (
                <div key={`docs-${payment.id}`} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        payment.paymentParty === 'SUPPLIER'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {t(`payments.partyTypes.${payment.paymentParty}`)}
                      </span>
                      <span className="text-sm font-medium text-gray-700">
                        {t('documents.paymentDocuments')} — {format(new Date(payment.date), 'dd/MM/yyyy')} · {formatCurrency(payment.amount)} đ
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setOpenUploadPaymentIds(prev => {
                          const next = new Set(prev);
                          if (next.has(payment.id)) {
                            next.delete(payment.id);
                          } else {
                            next.add(payment.id);
                          }
                          return next;
                        });
                      }}
                      className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded transition-colors ${
                        isUploadOpen
                          ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                          : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                      }`}
                    >
                      <Plus className="w-3 h-3" />
                      {isUploadOpen ? t('common.cancel') : t('documents.addDocument')}
                    </button>
                  </div>

                  <DocumentList
                    entityType="payment"
                    entityId={payment.id}
                    canDelete={true}
                    title=""
                    refreshTrigger={paymentDocumentRefreshKeys[payment.id]}
                  />

                  {isUploadOpen && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <DocumentUpload
                        entityType="payment"
                        entityId={payment.id}
                        documentType="PAYMENT_PROOF"
                        onUploadComplete={() => {
                          setPaymentDocumentRefreshKeys(prev => ({
                            ...prev,
                            [payment.id]: (prev[payment.id] || 0) + 1,
                          }));
                          // Auto-close upload panel after successful upload
                          setOpenUploadPaymentIds(prev => {
                            const next = new Set(prev);
                            next.delete(payment.id);
                            return next;
                          });
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-gray-500 py-4">{t('common.noData')}</p>
        )}
      </div>

      {/* Documents Section */}
      <div className="card">
        <div className="flex items-center space-x-2 mb-4">
          <FileText className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold">{t('documents.attachedDocuments')}</h2>
        </div>
        <DocumentList
          entityType="trip"
          entityId={trip.id}
          canDelete={true}
          title=""
        />
      </div>

      {/* Phase 2: Warehouse Receipt Confirmation Modal */}
      {trip && (
        <ReceiptConfirmationModal
          open={showReceiptModal}
          trip={trip}
          onConfirm={handleReceiptConfirm}
          onSkip={handleReceiptSkip}
          onClose={() => setShowReceiptModal(false)}
        />
      )}

      {/* Leftover goods modal for direct trips */}
      <LeftoverWarehouseModal
        isOpen={showLeftoverModal}
        leftovers={leftoverItems}
        onConfirm={handleLeftoverConfirm}
        onSkip={handleLeftoverSkip}
        onCancel={() => setShowLeftoverModal(false)}
        loading={completingLoading}
      />
    </div>
  );
};
